import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Action, Location } from 'history';

import { ScrollRestorationContext, ScrollRestorationContextValue } from '../../context/scrollRestoration';
import { isScrollPositionReachable, restoreScrollPosition, ScrollRestore } from '../../lib/restoreScrollPosition';
import { createScrollPositionCache } from '../../lib/scrollPositionCache';
import { createVirtualHistoryStack } from '../../lib/virtualHistoryStack';

// Entries of the browser's history are tagged with a key in window.history.state, to tell them apart (see
// classifyNavigation). Hash history never reads or writes history.state, so this doesn't interfere with routing.
const ENTRY_KEY_FIELD = 'elemezScrollKey';

/**
 * The most that each of these holds on to, so that none of them grows for as long as the app stays open: the positions
 * that pages were left at by history entry, the positions by url, and the history entries that are known about. Beyond
 * that the one that has gone unused for longest is forgotten, and a page that has been forgotten is shown at the top,
 * like any other page that has no position remembered.
 */
export const MAX_REMEMBERED_PAGES = 100;

export type NavigationKind = 'traversal' | 'new-entry' | 'same-entry';

/**
 * Works out what a navigation did to the browser's history, from the key of the history entry that is now current.
 *
 * The router's action can't tell: hash history reports every hash change that it didn't make itself as a POP, not
 * only the browser's Back and Forward but also `<a href="#/...">` links (e.g. in the user menu) and the urls that
 * charts navigate to. Those create new entries, though, and a new entry has no key yet:
 *  - no key: an entry never seen before, so a new page, whatever the action;
 *  - the key of the entry that was current: nothing moved (e.g. a link to the page the user is already on);
 *  - any other key: the user went back or forward to an entry that they had visited before.
 */
export const classifyNavigation = (entryKey: string | undefined, currentKey: string): NavigationKind => {
  if (entryKey === undefined) {
    return 'new-entry';
  }
  return entryKey === currentKey ? 'same-entry' : 'traversal';
};

let entryKeyCounter = 0;

// unique within the tab, including across reloads, which history.state survives
const createEntryKey = (): string => `${Date.now().toString(36)}-${(entryKeyCounter++).toString(36)}`;

const readEntryKey = (): string | undefined => window.history.state?.[ENTRY_KEY_FIELD];

const tagCurrentEntry = (): string => {
  const key = createEntryKey();
  try {
    window.history.replaceState({ ...window.history.state, [ENTRY_KEY_FIELD]: key }, '');
  } catch {
    // Safari limits how often replaceState may be called. The entry stays untagged, so coming back to it later
    // is treated as arriving at a new page (at the top) rather than failing the navigation.
  }
  return key;
};

const toUrl = ({ pathname, search }: Location): string => pathname + search;

/**
 * Keeps track of where each page was scrolled to when it was left, and decides where to scroll to when a page is
 * shown:
 *  - to where it was left, when the user goes Back or Forward to it, or follows a breadcrumb up to it (see
 *    ScrollRestorationContext);
 *  - to the top, for every other navigation: links, tabs, the sidebar...
 *
 * Positions are only remembered for so many pages (see MAX_REMEMBERED_PAGES). Those of the history entries that the
 * browser discards, as it does the ones after the current entry when a new one is made, are forgotten as soon as that
 * happens, since they can't be gone back to any more.
 *
 * An entry without a key is taken to be a new one, and an entry from before mounting that is gone Back to has none
 * either. So that is taken for a new entry too, and the positions of the entries after the page that was left are
 * forgotten, although they are still there: those pages are shown at the top instead.
 *
 * The router is not told of the browser moving between entries that have the same address, so those moves are picked
 * up from the browser's popstate event instead (see handlePopState).
 *
 * Replaces the browser's own scroll restoration, which can't be relied on here: it restores before the page has
 * rendered (and has nothing to restore into while it loads), and hash history can't tell it about the navigations
 * that it makes itself.
 */
export const useScrollRestoration = (): ScrollRestorationContextValue => {
  const routerHistory = useHistory();
  const location = useLocation();

  // the location last navigated to, and the key of its entry in the browser's history - whether rendered or not
  const currentLocation = React.useRef<Location>(null);
  const currentKey = React.useRef<string>(null);
  // the address that the browser showed for that location, to tell when it moves to an entry that has the same one
  const currentHref = React.useRef<string>(null);
  // whether that location has been rendered and scrolled, or the window still shows the previous page
  const currentRendered = React.useRef(false);
  // where pages were scrolled to when they were left: by history entry, for Back and Forward, and by url, for breadcrumbs
  const keyPositions = React.useRef(createScrollPositionCache(MAX_REMEMBERED_PAGES));
  const urlPositions = React.useRef(createScrollPositionCache(MAX_REMEMBERED_PAGES));
  // The entries in the browser's history that are known about, to tell which of them a new entry discards. The browser
  // doesn't say, so they are the ones seen since mounting. The positions of discarded entries are forgotten, but the
  // positions by url are not: a breadcrumb goes back to one whether or not the entry that it was left from is still there.
  const entries = React.useRef(createVirtualHistoryStack(MAX_REMEMBERED_PAGES));
  const breadcrumbUrl = React.useRef<string>(null);
  // where to scroll to once the location navigated to has been rendered
  const pendingTarget = React.useRef(0);
  const restore = React.useRef<ScrollRestore>(null);
  // Counts the times that the browser moves to another entry of the location that is shown (see handlePopState). The
  // router doesn't render it again for that, but the page is scrolled just as it is after any other navigation.
  const [entryMoves, countEntryMove] = React.useReducer((count: number) => count + 1, 0);

  // Moves on from the page being shown to the location that the browser has moved to: remembers where the page was
  // left, and works out where to scroll to once the location has rendered. Until then the window still shows the page
  // being left.
  const moveTo = React.useCallback((nextLocation: Location, action: Action) => {
    // a restore that hasn't got there yet (the page is not tall enough yet): the window is short of where it was heading
    const outgoingPosition = restore.current?.isPending() ? restore.current.target : window.scrollY;
    restore.current?.cancel();
    restore.current = null;

    const requestedUrl = breadcrumbUrl.current;
    breadcrumbUrl.current = null;

    const entryKey = readEntryKey();
    const kind = classifyNavigation(entryKey, currentKey.current);
    const nextUrl = toUrl(nextLocation);
    // A replacement takes the place of the current entry, instead of leaving it for a new one, so the entries after it
    // are still there. Like a new entry it has no state, so only the router's action tells it apart.
    const replacement = kind === 'new-entry' && action === 'REPLACE';

    // Looked up before the page being left is remembered. The positions that are remembered are limited, so remembering
    // one can make another be forgotten, and that could be the one that is being looked up.
    let target = 0;
    if (kind === 'traversal') {
      target = keyPositions.current.get(entryKey) ?? 0;
    } else if (kind === 'new-entry' && requestedUrl === nextUrl) {
      target = urlPositions.current.get(nextUrl) ?? 0;
    }

    // a location that was replaced by a redirect before it was ever rendered has no scroll position
    if (currentRendered.current) {
      // the entry that is replaced is gone, and its key is of no use any more
      if (!replacement) {
        keyPositions.current.set(currentKey.current, outgoingPosition);
      }
      urlPositions.current.set(toUrl(currentLocation.current), outgoingPosition);
    }

    if (kind === 'traversal') {
      currentKey.current = entryKey;
      entries.current.traverseTo(entryKey);
    } else if (kind === 'new-entry') {
      currentKey.current = tagCurrentEntry();
      if (replacement) {
        keyPositions.current.delete(entries.current.replace(currentKey.current));
      } else {
        // the browser discards the entries after the one that a new entry is made from, so they can't be gone to now
        entries.current.push(currentKey.current).forEach(key => keyPositions.current.delete(key));
      }
    }

    pendingTarget.current = target;
    currentLocation.current = nextLocation;
    currentHref.current = window.location.href;
    currentRendered.current = false;
  }, []);

  // Called synchronously by the router for every navigation - before the new page is rendered, so the window still
  // shows the page being left
  const handleNavigation = React.useCallback((nextLocation: Location, action: Action) => {
    // not a navigation: a Prompt that cancelled one, or a push to the path already shown
    if (nextLocation === currentLocation.current) {
      return;
    }

    moveTo(nextLocation, action);
  }, [moveTo]);

  // The router only hears of the browser moving to another entry from the address changing, so it hears nothing when
  // the entry that the browser moves to has the same address as the one that is shown. That happens, for instance, when
  // the history is gone back over the page that a breadcrumb came from, to the earlier visit of the page that it led
  // back to. The location is the same one, and has been rendered already, so what is left to do is to scroll to where
  // that entry was left.
  //
  // A move to an entry with another address has changed the address by the time that popstate is fired, and is left to
  // the router to tell handleNavigation of, so that it is only ever handled once. And an entry without a key, or with
  // the key of the entry that is shown, is not a move to another entry that is known of (see classifyNavigation).
  const handlePopState = React.useCallback(() => {
    if (
      window.location.href !== currentHref.current ||
      classifyNavigation(readEntryKey(), currentKey.current) !== 'traversal'
    ) {
      return;
    }

    moveTo(currentLocation.current, 'POP');
    // The router renders nothing for it, so what scrolls the page once it has rendered is to run for it as well
    countEntryMove();
  }, [moveTo]);

  React.useLayoutEffect(() => {
    // 'manual' where the browser supports it, otherwise left alone
    const previousMode = window.history.scrollRestoration;
    if (previousMode) {
      window.history.scrollRestoration = 'manual';
    }

    // not the location that rendered, if a redirect has already changed it
    currentLocation.current = routerHistory.location;
    currentHref.current = window.location.href;
    currentKey.current = readEntryKey() ?? tagCurrentEntry();
    entries.current.startAt(currentKey.current);
    const unlisten = routerHistory.listen(handleNavigation);
    window.addEventListener('popstate', handlePopState);

    return () => {
      unlisten();
      window.removeEventListener('popstate', handlePopState);
      restore.current?.cancel();
      if (previousMode) {
        window.history.scrollRestoration = previousMode;
      }
    };
  }, [routerHistory, handleNavigation, handlePopState]);

  // Scrolls as soon as the page has rendered, before the browser paints it, so that it never shows at the wrong place
  React.useLayoutEffect(() => {
    const target = pendingTarget.current;
    pendingTarget.current = 0;
    // A page that is not tall enough yet for the position would only be scrolled as far as it goes, which is where it
    // would be painted while it loads, before it is moved again once it has grown. It is shown at the top instead.
    window.scrollTo(0, isScrollPositionReachable(target) ? target : 0);
    currentRendered.current = location === currentLocation.current;
    if (target > 0) {
      // the page may not be tall enough yet for the window to have got there: this carries on until it is
      restore.current = restoreScrollPosition(target);
    }
  }, [location, entryMoves]);

  const restoreOnNextNavigation = React.useCallback((url: string) => {
    breadcrumbUrl.current = url;
    // The navigation that this is for happens synchronously, within the same event. A click that doesn't navigate (a
    // modified one, or one that was prevented) must not leave the request behind for an unrelated navigation later.
    Promise.resolve().then(() => {
      if (breadcrumbUrl.current === url) {
        breadcrumbUrl.current = null;
      }
    });
  }, []);

  return React.useMemo(() => ({ restoreOnNextNavigation }), [restoreOnNextNavigation]);
};

export const ScrollRestoration = ({ children }: { children: React.ReactNode }) => {
  const value = useScrollRestoration();

  return (
    <ScrollRestorationContext.Provider value={value}>
      {children}
    </ScrollRestorationContext.Provider>
  );
};
